import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useRefreshKentuckyPO } from '@/hooks/usePOMutations'
import type { components } from '@/lib/api/generated'
import { cn } from '@/lib/utils'

type PO = components['schemas']['POResponse']

// Kentucky Horsewear transit panel — Order ref / Status / Transporter / Tracking
// plus a Refresh that pulls the latest from Kentucky (Arana). Shared by the PO
// expanded-row detail and the Receiving page. Renders nothing until the PO has
// actually been transmitted (carries an order reference).
export function KentuckyPanel({ po, className }: { po: PO; className?: string }) {
  const refreshKentucky = useRefreshKentuckyPO()

  if (!po.is_kentucky || !po.arana_order_reference) return null

  function onRefresh() {
    refreshKentucky.mutate(po.po_id, {
      onSuccess: (res) => {
        if (res.shipped) {
          toast.success('Updated from Kentucky')
        } else {
          toast.message(res.message || 'Not shipped yet')
        }
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to refresh from Kentucky'),
    })
  }

  return (
    <div
      className={cn(
        'rounded-md border border-[var(--line-warm)] bg-[var(--surface-card)] px-3 py-2',
        className
      )}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-primary)]">
          Kentucky Horsewear
        </span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[11px]"
          onClick={onRefresh}
          disabled={refreshKentucky.isPending}
        >
          {refreshKentucky.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px]">
        <span className="text-[var(--mute)]">
          Order ref{' '}
          <span className="text-[var(--ink-2)] tabular-nums">{po.arana_order_reference}</span>
        </span>
        <span className="text-[var(--mute)]">
          Status <span className="text-[var(--ink-2)]">{po.arana_status || '—'}</span>
        </span>
        <span className="text-[var(--mute)]">
          Transporter{' '}
          <span className="text-[var(--ink-2)]">{po.arana_transporter || 'Not shipped yet'}</span>
        </span>
        <span className="text-[var(--mute)]">
          Tracking{' '}
          <span className="text-[var(--ink-2)] tabular-nums">{po.arana_tracking_number || '—'}</span>
        </span>
      </div>
    </div>
  )
}
